import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Plus, Copy, Check, Mail, RefreshCw, Ban,
  UserX, UserCheck, ArrowRight, Pencil, X,
} from "lucide-react";
import { api, formatApiError } from "@/auth/AuthContext";
import { AdminShell, StatusBadge, TYPE_LABELS } from "@/pages/AdminPlatform";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

const Section = ({ title, children, action }) => (
  <section className="mt-8 bg-white border border-[#EAE6DF] p-6 md:p-8">
    <div className="flex items-center justify-between gap-4">
      <h2 className="font-heading text-xl text-[#0E2A5C]">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const CopyLinkButton = ({ link }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch { /* http fallback */
      const ta = document.createElement("textarea");
      ta.value = link; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} data-testid="copy-activation-link"
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-[#0E2A5C] hover:text-[#C62828] border border-[#EAE6DF] px-2.5 py-1.5 transition-colors">
      {copied ? <><Check size={11}/> Copied</> : <><Copy size={11}/> Copy Link</>}
    </button>
  );
};

const EditOrgModal = ({ org, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: org.name, legal_name: org.legal_name, country: org.country, city: org.city,
    address: org.address || "", contact_name: org.contact_name, contact_email: org.contact_email,
    contact_phone: org.contact_phone, website: org.website || "", registration_number: org.registration_number || "",
    preferred_currency: org.preferred_currency,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const { data } = await api.patch(`/admin/organizations/${org.id}`, form);
      onSaved(data.organization);
    } catch (err) { setError(formatApiError(err)); setLoading(false); }
  };

  const fields = [
    ["name", "Organization Name"], ["legal_name", "Legal Company Name"], ["country", "Country"],
    ["city", "City"], ["address", "Address"], ["contact_name", "Contact Name"],
    ["contact_email", "Contact Email"], ["contact_phone", "Contact Phone"],
    ["website", "Website"], ["registration_number", "Trade License / Reg. No."],
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4 md:p-10">
      <form onSubmit={submit} className="bg-white border border-[#EAE6DF] w-full max-w-xl p-8 relative" data-testid="edit-org-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#4A4A4A] hover:text-[#C62828]"><X size={18}/></button>
        <h2 className="font-heading text-2xl text-[#0E2A5C]">Edit Organization</h2>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          {fields.map(([k, label]) => (
            <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A]">{label}</label>
              <input value={form[k]} onChange={set(k)} data-testid={`edit-${k}`}
                className="mt-1 w-full px-3.5 py-2.5 border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm"/>
            </div>
          ))}
        </div>
        {error && <p className="mt-4 text-sm text-[#C62828]">{error}</p>}
        <button type="submit" disabled={loading} data-testid="edit-org-save"
          className="mt-6 w-full h-[46px] bg-[#0E2A5C] hover:bg-[#0A1F44] disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2">
          {loading ? <Loader2 size={15} className="animate-spin"/> : "Save Changes"}
        </button>
      </form>
    </div>
  );
};

export default function AdminOrgDetail() {
  const { orgId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesSaved, setNotesSaved] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "" });
  const [inviteError, setInviteError] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/organizations/${orgId}`);
      setData(res.data);
      setNotes(res.data.organization.internal_notes || "");
      if (!res.data.role_options.includes(inviteForm.role)) {
        setInviteForm((f) => ({ ...f, role: res.data.role_options[0] }));
      }
    } catch (e) { setErr(formatApiError(e)); }
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const patchOrg = async (updates) => {
    const res = await api.patch(`/admin/organizations/${orgId}`, updates);
    setData((d) => ({ ...d, organization: res.data.organization }));
  };

  const invite = async (e) => {
    e.preventDefault();
    setInviteLoading(true); setInviteError(null);
    try {
      await api.post(`/admin/organizations/${orgId}/users`, inviteForm);
      setInviteForm((f) => ({ ...f, name: "", email: "" }));
      await load();
    } catch (er) { setInviteError(formatApiError(er)); }
    finally { setInviteLoading(false); }
  };

  const doAction = async (fn, okMsg) => {
    try { await fn(); setActionMsg(okMsg); setTimeout(() => setActionMsg(null), 2500); await load(); }
    catch (e) { setActionMsg(formatApiError(e)); setTimeout(() => setActionMsg(null), 3500); }
  };

  if (err) {
    return (
      <AdminShell title="Organization">
        <p className="text-[#C62828]">{err}</p>
        <Link to="/admin" className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C]"><ArrowLeft size={13}/> Back to dashboard</Link>
      </AdminShell>
    );
  }
  if (!data) {
    return <AdminShell title="Organization"><div className="py-16 text-center"><Loader2 className="animate-spin text-[#C62828] mx-auto" size={26}/></div></AdminShell>;
  }

  const { organization: org, users, invitations, role_options, role_labels } = data;

  return (
    <AdminShell title={org.name}>
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] hover:text-[#0E2A5C]" data-testid="back-to-admin">
        <ArrowLeft size={13}/> All organizations
      </Link>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl md:text-4xl text-[#0E2A5C]" data-testid="org-detail-name">{org.name}</h1>
            <StatusBadge status={org.status}/>
          </div>
          <p className="mt-1 text-sm text-[#4A4A4A]">{TYPE_LABELS[org.account_type]} · {org.city}, {org.country}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowEdit(true)} data-testid="edit-org-button"
            className="inline-flex items-center gap-1.5 border border-[#0E2A5C] text-[#0E2A5C] hover:bg-[#0E2A5C] hover:text-white px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold transition-colors">
            <Pencil size={12}/> Edit
          </button>
          {org.status !== "active" && (
            <button onClick={() => doAction(() => patchOrg({ status: "active" }), "Organization activated")} data-testid="activate-org-button"
              className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold transition-colors">
              <UserCheck size={12}/> Activate
            </button>
          )}
          {org.status === "active" && (
            <button onClick={() => doAction(() => patchOrg({ status: "suspended" }), "Organization suspended")} data-testid="suspend-org-button"
              className="inline-flex items-center gap-1.5 bg-[#C62828] hover:bg-[#B71C1C] text-white px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold transition-colors">
              <Ban size={12}/> Suspend
            </button>
          )}
        </div>
      </div>

      {actionMsg && <p className="mt-3 text-sm text-[#0E2A5C] bg-white border-l-2 border-[#D4AF37] p-2.5" data-testid="org-action-msg">{actionMsg}</p>}

      {/* Overview */}
      <Section title="Overview">
        <dl className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
          {[
            ["Legal Name", org.legal_name], ["Account Type", TYPE_LABELS[org.account_type]],
            ["Country", org.country], ["City", org.city], ["Address", org.address || "—"],
            ["Contact", org.contact_name], ["Email", org.contact_email], ["Phone", org.contact_phone],
            ["Website", org.website || "—"], ["Trade License / Reg. No.", org.registration_number || "—"],
            ["Preferred Currency", org.preferred_currency], ["Created", fmtDate(org.created_at)],
          ].map(([k, v]) => (
            <div key={k} className="border-b border-[#EAE6DF]/70 pb-2">
              <dt className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#4A4A4A]">{k}</dt>
              <dd className="mt-0.5">{v}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* Users */}
      <Section title={`Users (${users.length})`}>
        <div className="mt-4 overflow-x-auto">
          {users.length === 0 ? (
            <p className="text-sm text-[#4A4A4A]" data-testid="users-empty">No users yet — invite the primary user below.</p>
          ) : (
            <table className="w-full text-sm" data-testid="users-table">
              <thead><tr className="border-b border-[#EAE6DF] text-left">
                {["Name","Email","Role","Status","Last Login","Actions"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#4A4A4A]">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-[#EAE6DF]/60 last:border-0" data-testid={`user-row-${u.email}`}>
                    <td className="px-3 py-3 font-semibold text-[#0E2A5C]">{u.name}</td>
                    <td className="px-3 py-3">{u.email}</td>
                    <td className="px-3 py-3">
                      <select value={u.role} data-testid={`role-select-${u.email}`}
                        onChange={(e) => doAction(() => api.patch(`/admin/users/${u.id}`, { role: e.target.value }), "Role updated")}
                        className="border border-[#EAE6DF] px-2 py-1.5 text-xs focus:outline-none bg-white">
                        {role_options.map((r) => <option key={r} value={r}>{role_labels[r] || r}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-3"><StatusBadge status={u.status}/></td>
                    <td className="px-3 py-3 text-[#4A4A4A] text-xs">{fmtDate(u.last_login_at)}</td>
                    <td className="px-3 py-3">
                      {u.status === "active" && (
                        <button onClick={() => doAction(() => api.patch(`/admin/users/${u.id}`, { status: "suspended" }), "User suspended")}
                          data-testid={`suspend-user-${u.email}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-bold text-[#C62828] hover:underline">
                          <UserX size={11}/> Suspend
                        </button>
                      )}
                      {u.status === "suspended" && (
                        <button onClick={() => doAction(() => api.patch(`/admin/users/${u.id}`, { status: "active" }), "User reactivated")}
                          data-testid={`activate-user-${u.email}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-bold text-emerald-700 hover:underline">
                          <UserCheck size={11}/> Activate
                        </button>
                      )}
                      {u.status === "invited" && <span className="text-[10px] uppercase tracking-[0.1em] text-[#4A4A4A]">Awaiting activation</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Invite user */}
        <form onSubmit={invite} className="mt-6 border-t border-[#EAE6DF] pt-6" data-testid="invite-user-form">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C62828]">✦ Add User & Send Invitation</p>
          <div className="mt-3 grid sm:grid-cols-4 gap-3">
            <input value={inviteForm.name} required placeholder="Full name" data-testid="invite-name"
              onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))}
              className="px-3.5 py-2.5 border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm"/>
            <input value={inviteForm.email} required type="email" placeholder="Business email" data-testid="invite-email"
              onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
              className="px-3.5 py-2.5 border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm"/>
            <select value={inviteForm.role} data-testid="invite-role"
              onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}
              className="px-3.5 py-2.5 border border-[#EAE6DF] focus:outline-none text-sm bg-white">
              {role_options.map((r) => <option key={r} value={r}>{role_labels[r] || r}</option>)}
            </select>
            <button type="submit" disabled={inviteLoading} data-testid="send-invitation-button"
              className="h-[42px] bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-60 text-white text-[11px] uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 transition-colors">
              {inviteLoading ? <Loader2 size={14} className="animate-spin"/> : <><Plus size={13}/> Send Invitation</>}
            </button>
          </div>
          {inviteError && <p className="mt-2 text-sm text-[#C62828]" data-testid="invite-error">{inviteError}</p>}
        </form>
      </Section>

      {/* Invitations */}
      <Section title={`Invitations (${invitations.length})`}>
        <div className="mt-4 overflow-x-auto">
          {invitations.length === 0 ? (
            <p className="text-sm text-[#4A4A4A]">No invitations yet.</p>
          ) : (
            <table className="w-full text-sm" data-testid="invitations-table">
              <thead><tr className="border-b border-[#EAE6DF] text-left">
                {["Email","Status","Email Delivery","Expires","Actions"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#4A4A4A]">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {invitations.map((inv) => (
                  <tr key={inv.id} className="border-b border-[#EAE6DF]/60 last:border-0" data-testid={`invitation-row-${inv.email}`}>
                    <td className="px-3 py-3">{inv.email}</td>
                    <td className="px-3 py-3"><StatusBadge status={inv.status}/></td>
                    <td className="px-3 py-3">
                      {inv.status === "pending" && !inv.email_sent ? (
                        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1" data-testid="email-pending-badge">
                          <Mail size={10}/> Email pending — copy link
                        </span>
                      ) : inv.email_sent ? (
                        <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-emerald-700">Sent</span>
                      ) : <span className="text-[#4A4A4A]">—</span>}
                    </td>
                    <td className="px-3 py-3 text-xs text-[#4A4A4A]">{fmtDate(inv.expires_at)}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {inv.status === "pending" && inv.activation_link && <CopyLinkButton link={inv.activation_link}/>}
                        {inv.status !== "accepted" && (
                          <button onClick={() => doAction(() => api.post(`/admin/invitations/${inv.id}/resend`), "Invitation resent")}
                            data-testid={`resend-invitation-${inv.email}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-bold text-[#0E2A5C] hover:underline">
                            <RefreshCw size={11}/> Resend
                          </button>
                        )}
                        {inv.status === "pending" && (
                          <button onClick={() => doAction(() => api.post(`/admin/invitations/${inv.id}/revoke`), "Invitation revoked")}
                            data-testid={`revoke-invitation-${inv.email}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-bold text-[#C62828] hover:underline">
                            <Ban size={11}/> Revoke
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Section>

      {/* Future-ready */}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="border border-dashed border-[#EAE6DF] bg-white/60 p-6" data-testid="commercial-settings-card">
          <h3 className="font-heading text-lg text-[#0E2A5C]">Commercial Settings</h3>
          <p className="text-sm text-[#4A4A4A] mt-1.5">Net fares, markup rules, service fees, credit terms, booking limits and travel policy will be configured here.</p>
          <p className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold mt-3">Future Ready</p>
        </div>
        <div className="border border-dashed border-[#EAE6DF] bg-white/60 p-6" data-testid="bookings-card">
          <h3 className="font-heading text-lg text-[#0E2A5C]">Bookings</h3>
          <p className="text-sm text-[#4A4A4A] mt-1.5">This organization&apos;s flight and travel bookings will appear here after live booking is activated.</p>
          <p className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold mt-3">Future Ready</p>
        </div>
      </div>

      {/* Internal notes */}
      <Section title="Internal Notes" action={<span className="text-[9px] uppercase tracking-[0.2em] text-[#4A4A4A] font-semibold">Admins only</span>}>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="internal-notes"
          className="mt-4 w-full px-4 py-3 border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm"/>
        <button onClick={() => doAction(async () => { await patchOrg({ internal_notes: notes }); setNotesSaved(true); setTimeout(() => setNotesSaved(false), 2000); }, "Notes saved")}
          data-testid="save-notes-button"
          className="mt-3 inline-flex items-center gap-2 bg-[#0E2A5C] hover:bg-[#0A1F44] text-white px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold transition-colors">
          {notesSaved ? <><Check size={13}/> Saved</> : "Save Notes"}
        </button>
      </Section>

      {showEdit && <EditOrgModal org={org} onClose={() => setShowEdit(false)} onSaved={(o) => { setData((d) => ({ ...d, organization: o })); setShowEdit(false); }}/>}
    </AdminShell>
  );
}
